import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ptBR } from 'date-fns/locale';
import { Client, Contract, Invoice } from '../../types';
import { InvoiceFormData } from '../../services/invoiceService';
import { clientService } from '../../services/clientService';
import invoiceService from '../../services/invoiceService';
import GatewaySelect from '../selects/GatewaySelect';
import ClientSelect from '../selects/ClientSelect';
import { Gateway } from '../../services/gatewayService';
import { formatarValorOnInput } from '../../utils/formatToView';

interface InvoiceFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: InvoiceFormData) => Promise<void>;
  invoice?: Invoice | null;
  title?: string;
  clientId?: number; // Cliente pré-selecionado (vindo da página do cliente)
  contractId?: number; // Contrato pré-selecionado (vindo da página do contrato)
}

// Função para obter data atual no formato YYYY-MM-DD
const getCurrentDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const InvoiceEditForm: React.FC<InvoiceFormProps> = ({
  open,
  onClose,
  onSubmit,
  invoice,
  title = 'Nova Fatura',
  clientId,
  contractId
}) => {
  const [formData, setFormData] = useState<InvoiceFormData>({
    cliente_id: 0,
    contrato_id: null,
    data_vencimento: getCurrentDate(),
    tipo: 'mensalidade',
    valor: 0,
    acrescimo: 0,
    desconto: 0,
    descricao: '',
    mes_referencia: invoiceService.getCurrentReferenceMonth(),
    observacoes: '',
    gateway_id: null,
    naogeranfcom: null
  });

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Estados para valores formatados em moeda
  const [valorFormatado, setValorFormatado] = useState('0,00');
  const [acrescimoFormatado, setAcrescimoFormatado] = useState('0,00');
  const [descontoFormatado, setDescontoFormatado] = useState('0,00');

  // Estado para controlar se a fatura é parcialmente paga
  const [isPartiallyPaid, setIsPartiallyPaid] = useState(false);


  // Função auxiliar para criar Date a partir de string YYYY-MM-DD sem problemas de fuso horário
  const criarDataSegura = (dataString: string): Date | null => {
    if (!dataString || dataString === '') return null;
    
    try {
      // Separar os componentes da data
      const [year, month, day] = dataString.split('-').map(num => parseInt(num, 10));
      
      // Validar se os valores são válidos
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      if (year < 1900 || year > 2100) return null;
      if (month < 1 || month > 12) return null;
      if (day < 1 || day > 31) return null;
      
      // Criar Date usando construtor com componentes (evita problemas de fuso horário)
      const date = new Date(year, month - 1, day); // month é 0-based
      
      // Verificar se a data criada é válida
      if (isNaN(date.getTime())) return null;
      
      // Verificar se os componentes correspondem (previne datas como 31/02)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
      }
      
      return date;
    } catch (error) {
      console.error('Erro ao criar data segura:', error);
      return null;
    }
  };

  // Função auxiliar para formatar Date para string YYYY-MM-DD
  const formatarDataSegura = (date: Date): string => {
    if (!date || isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Função para formatar valor para moeda brasileira
  const formatarMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };


  // Carregar contratos quando cliente for selecionado
  useEffect(() => {
    const loadContracts = async () => {
      if (formData.cliente_id > 0) {
        try {
          const response = await invoiceService.getClientContracts(formData.cliente_id);
          if (response.success) {
            setContracts(response.data || []);
          }
        } catch (error) {
          console.error('Erro ao carregar contratos:', error);
          setContracts([]);
        }
      } else {
        setContracts([]);
      }
    };

    loadContracts();
  }, [formData.cliente_id]);

  // Preencher formulário quando editando ou quando há contexto pré-definido
  useEffect(() => {
    if (invoice && open) {
      // Editando fatura existente
      const valorNum = Number(invoice.valor) || 0;
      const acrescimoNum = Number(invoice.acrescimo) || 0;
      const descontoNum = Number(invoice.desconto) || 0;

      // Validar e formatar data de vencimento
      let dataVencimento = '';
      if (invoice.data_vencimento) {
        try {
          // Primeiro tentar como string já formatada (YYYY-MM-DD)
          if (typeof invoice.data_vencimento === 'string' && invoice.data_vencimento.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dataVencimento = invoice.data_vencimento;
          } else {
            // Se não, tentar converter de ISO ou outro formato
            const date = new Date(invoice.data_vencimento);
            if (!isNaN(date.getTime())) {
              dataVencimento = formatarDataSegura(date);
            }
          }
        } catch (error) {
          console.error('Erro ao processar data de vencimento:', error);
          dataVencimento = '';
        }
      }

      setFormData({
        cliente_id: invoice.cliente_id,
        contrato_id: invoice.contrato_id || null,
        data_vencimento: dataVencimento,
        tipo: invoice.tipo,
        valor: valorNum,
        acrescimo: acrescimoNum,
        desconto: descontoNum,
        descricao: invoice.descricao,
        mes_referencia: invoice.mes_referencia,
        observacoes: invoice.observacoes || '',
        gateway_id: invoice.gateway_id || null,
        naogeranfcom: (invoice as any)?.naogeranfcom ?? null
      });

      // Formatar valores para exibição
      setValorFormatado(formatarMoeda(valorNum));
      setAcrescimoFormatado(formatarMoeda(acrescimoNum));
      setDescontoFormatado(formatarMoeda(descontoNum));

      // Definir cliente selecionado (será carregado pelo ClientSelect)
      setSelectedClient(null);

      // Verificar se a fatura é parcialmente paga baseado no status
      setIsPartiallyPaid(invoice.status === 'parcialmente_pago');
    } else if (open) {
      // Nova fatura - considerar contexto (clientId/contractId)
      const initialClientId = clientId || 0;
      const initialContractId = contractId || null;
      
      setFormData({
        cliente_id: initialClientId,
        contrato_id: initialContractId,
        data_vencimento: getCurrentDate(),
        tipo: 'mensalidade',
        valor: 0,
        acrescimo: 0,
        desconto: 0,
        descricao: '',
        mes_referencia: invoiceService.getCurrentReferenceMonth(),
        observacoes: '',
        gateway_id: null,
        naogeranfcom: null
      });

      // Resetar valores formatados
      setValorFormatado('0,00');
      setAcrescimoFormatado('0,00');
      setDescontoFormatado('0,00');

      // Resetar estado de fatura parcialmente paga
      setIsPartiallyPaid(false);

      // Se clientId foi fornecido, definir como selecionado
      if (clientId) {
        setSelectedClient({ id: clientId } as Client);
      } else {
        setSelectedClient(null);
      }
    }
  }, [invoice, open, clientId, contractId]);

  // Efeito para preencher valor e descrição automaticamente quando contrato for selecionado
  useEffect(() => {
    // Só preencher automaticamente se não estivermos editando uma fatura existente
    if (!invoice && formData.contrato_id && contracts.length > 0) {
      const selectedContract = contracts.find(c => c.id === formData.contrato_id);
      
      if (selectedContract && selectedContract.valor_contratado !== undefined) {
        const valorContrato = Number(selectedContract.valor_contratado) || 0;
        const valorAcrescimo = Number(selectedContract.acrescimo) || 0;
        const valorDesconto = Number(selectedContract.desconto) || 0;
       
        // Atualizar valor e gateway
        setFormData(prev => ({
          ...prev,
          valor: valorContrato,
          acrescimo: valorAcrescimo,
          desconto: valorDesconto,
          gateway_id: selectedContract.gateway_id || null          
        }));
        setValorFormatado(formatarMoeda(valorContrato));
        setAcrescimoFormatado(formatarMoeda(valorAcrescimo));
        setDescontoFormatado(formatarMoeda(valorDesconto));
        
        // Atualizar descrição baseado no plano ou título do contrato
        let novaDescricao = '';
        if (selectedContract.plano && selectedContract.plano.nome) {
          novaDescricao = selectedContract.plano.nome;
        } else {
          novaDescricao = selectedContract.titulo;
        }
        
        setFormData(prev => ({
          ...prev,
          descricao: novaDescricao
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          valor: 0,
          acrescimo: 0,
          desconto: 0,
          gateway_id: null,
          naogeranfcom: null
        }));
        setValorFormatado('0,00');
        setAcrescimoFormatado('0,00');
        setDescontoFormatado('0,00');
      }
    }
  }, [formData.contrato_id, contracts, invoice]);

  // TODO: Resolver problema de tipagem TypeScript e reabilitar esta funcionalidade
  // Atualizar resumo de pagamentos quando valores da fatura mudarem
  /*
  useEffect(() => {
    if (invoice && paymentSummary && invoice.id && typeof invoice.id === 'number') {
      const invoiceId: number = invoice.id; // Type assertion explícita
      const newBasicTotal = invoiceService.calculateTotal(formData.valor, formData.acrescimo, formData.desconto);
      const currentBasicTotal = paymentSummary.totalAmount;
      
      // Se o valor total básico da fatura mudou, recarregar resumo de pagamentos
      if (newBasicTotal !== currentBasicTotal) {
        loadPaymentSummary(invoiceId, formData.valor, formData.acrescimo, formData.desconto);
      }
    }
  }, [formData.valor, formData.acrescimo, formData.desconto]);
  */

  const handleInputChange = (field: keyof InvoiceFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClientChange = (client: Client | null) => {
    setSelectedClient(client);
    if (client) {
      handleInputChange('cliente_id', client.id);
      handleInputChange('contrato_id', null); // Reset contract when client changes
    } else {
      handleInputChange('cliente_id', 0);
      handleInputChange('contrato_id', null);
    }
  };

  // Handlers para campos de moeda
  const handleValorChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);
    
    setValorFormatado(valorFormatado);
    handleInputChange('valor', valorNumerico);
  };

  const handleAcrescimoChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);
    
    setAcrescimoFormatado(valorFormatado);
    handleInputChange('acrescimo', valorNumerico);
  };

  const handleDescontoChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);
    
    setDescontoFormatado(valorFormatado);
    handleInputChange('desconto', valorNumerico);
  };

  const handleGatewayChange = (gatewayId: number | null, gateway?: Gateway) => {
    handleInputChange('gateway_id', gatewayId);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors([]);

    try {
      // Validar dados
      const validationErrors = invoiceService.validateInvoiceData(formData);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
      }

      await onSubmit(formData);
    } catch (error: any) {
      console.error('Erro ao salvar fatura:', error);
      setErrors([error.message || 'Erro ao salvar fatura']);
    } finally {
      setLoading(false);
    }
  };

  const getBasicTotal = () => {
    return invoiceService.calculateTotal(formData.valor, formData.acrescimo, formData.desconto);
  };

  return (    
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5 }}>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}

          {/* Mostrar alerta se fatura for parcialmente paga */}
          {isPartiallyPaid && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: '0.9rem' }}>
              <Typography variant="h6" gutterBottom>
                Fatura Parcialmente Paga
              </Typography>
              <Typography variant="body2">
                Esta fatura possui pagamentos parciais e não pode ser editada. 
                Para alterar os dados da fatura, é necessário cancelar os pagamentos existentes primeiro.
              </Typography>
            </Alert>
          )}

          {/* Mostrar formulário apenas se não for parcialmente paga */}
          {!isPartiallyPaid && (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {/* Informação do Cliente Pré-selecionado */}
            {clientId && selectedClient && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Cliente:</strong> {selectedClient.nome} - {selectedClient.cpf_cnpj}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Cliente - só exibir se não foi fornecido clientId E não estiver editando */}
            {!clientId && !invoice && (
              <Grid item xs={12} md={12} sx={{p: 0.5}}>
                <ClientSelect
                  value={formData.cliente_id || null}
                  onChange={(clientId, client) => handleClientChange(client || null)}
                  label="Cliente"
                  placeholder="Digite para buscar um cliente"
                  required={true}
                  error={formData.cliente_id === 0}
                  helperText={formData.cliente_id === 0 ? 'Cliente é obrigatório' : ''}
                  size="small"
                  fullWidth={true}
                  onlyActive={true}
                  allowClear={true}
                />
              </Grid>
            )}

            {/* Contrato */}
            <Grid item xs={12} md={12} sx={{p: 0.5}}>
              <FormControl fullWidth size="small">
                <InputLabel>Contrato</InputLabel>
                <Select
                  value={formData.contrato_id || 'geral'}
                  onChange={(e) => handleInputChange('contrato_id', e.target.value === 'geral' ? null : e.target.value)}
                  label="Contrato"
                  size="small"
                  disabled={!formData.cliente_id || (!!contractId && !invoice)} // Desabilitar se contractId foi fornecido (exceto ao editar)
                >
                  
                  {contracts.map((contract) => (
                    <MenuItem key={contract.id} value={contract.id}>
                      {contract.titulo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Tipo */}
            <Grid item xs={12} md={6} sx={{p: 0.5}}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo *</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  label="Tipo *"
                  size="small"
                >
                  <MenuItem value="mensalidade">Mensalidade</MenuItem>
                  <MenuItem value="servicos">Serviços</MenuItem>
                  <MenuItem value="outros">Outros</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Data de Vencimento */}
            <Grid item xs={12} md={6} sx={{p: 0.5}}>
              
              <TextField
                label="Data de Vencimento *"
                type="date"
                fullWidth
                size="small"
                value={formData.data_vencimento}
                onChange={(e) => handleInputChange('data_vencimento', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!formData.data_vencimento}
                helperText={!formData.data_vencimento ? 'Data de vencimento é obrigatória' : ''}
              />
            </Grid>

            {/* Valor */}
            <Grid item xs={12} md={6} sx={{p: 0.5}}>
              <TextField
                label="Valor *"
                fullWidth
                size="small"
                value={valorFormatado}
                onChange={(e) => handleValorChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                placeholder="0,00"
              />
            </Grid>

            {/* Acréscimo */}
            <Grid item xs={12} md={6} sx={{p: 0.5}}>
              <TextField
                label="Acréscimo"
                fullWidth
                size="small"
                value={acrescimoFormatado}
                onChange={(e) => handleAcrescimoChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                placeholder="0,00"
              />
            </Grid>

            {/* Desconto */}
            <Grid item xs={12} md={6} sx={{p: 0.5}}>
              <TextField
                label="Desconto"
                fullWidth
                size="small"
                value={descontoFormatado}
                onChange={(e) => handleDescontoChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                placeholder="0,00"
              />
            </Grid>

            {/* Descrição */}
            <Grid item xs={12} sx={{p: 0.5}}>
              <TextField
                label="Descrição *"
                fullWidth
                size="small"
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
              />
            </Grid>

            {/* Gateway de Pagamento - só exibir se não estiver editando */}
            {!invoice && (
              <Grid item xs={12} md={6} sx={{p: 0.5}}>
                <GatewaySelect
                value={formData.gateway_id || undefined}
                onChange={handleGatewayChange}
                label="Gateway de Pagamento"
                required={false}
                  helperText="Gateway opcional para geração automática de boletos/PIX"
                  showOnlyActive={true}
                  size="medium"
                />
              </Grid>
            )}

            {/* Flag para não gerar NF (aparece em criação e edição) */}
            <Grid item xs={12} md={6} sx={{p: 0.5}}>
              <FormControlLabel 
                control={<Checkbox 
                  checked={formData.naogeranfcom === false}
                  onChange={(e) => handleInputChange('naogeranfcom', e.target.checked ? false : true)}
                />}
                label="Não gerar NFCom"
              />
            </Grid>

            {/* Mês de Referência */}
            <Grid item xs={12} md={invoice ? 12 : 6} sx={{p: 0.5}}>
              <TextField
                label="Mês de Referência *"
                fullWidth
                size="small"
                value={formData.mes_referencia}
                onChange={(e) => handleInputChange('mes_referencia', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                placeholder="MM-YYYY"
                helperText="Formato: MM-YYYY (ex: 01-2025)"
              />
            </Grid>

            {/* Valor Total */}
            <Grid item xs={12}>
              <Box sx={{ p: 0.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="h6" color="primary">
                  Valor Total: R$ {formatarMoeda(getBasicTotal())}
                </Typography>
              </Box>
            </Grid>

            {/* Observações */}
            <Grid item xs={12}>
              <TextField
                label="Observações"
                fullWidth
                size="small"
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
            Cancelar
          </Button>
          {!isPartiallyPaid && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
          )}
        </DialogActions>
      </Dialog>
  );
};

export default InvoiceEditForm; 